/**
 * Delivery schedule — single source of truth for delivery dates + cutoffs.
 *
 * Schedule (weekdays, lead days, cutoff hour) lives in theme settings / the
 * `delivery_schedule.week` shop metafield (see snippets/delivery-schedule.liquid).
 *
 * Public API (window.DeliverySchedule):
 *   next()              → first open delivery day (badge / marketing)
 *   listWindow()        → delivery days in the next 7 calendar days
 *   isDateAvailable()   → open for NEW shoppers (cutoff + manual off + future hooks)
 *   honorLockedDate()   → whether a cart-locked date is still orderable (§12)
 *   parseDisplayDate()  → "04.09.2026" → Date
 *   formatDisplayDate() → Date → "04.09.2026"
 *   formatTileLabel()   → short tile text
 *   formatSelectedLabel() → "piątek, 4 września"
 *   update()            → fill [data-delivery-date] / [data-delivery-cutoff] badges
 *
 * All maths use the SHOP timezone, not the shopper's clock.
 */
(function () {
  'use strict';

  if (window.DeliverySchedule) return;

  var CONFIG_ID = 'DeliveryScheduleConfig';
  var FALLBACK_TIMEZONE = 'Europe/Warsaw';
  var DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  var LOOKAHEAD_DAYS = 21;
  var DISPLAY_WINDOW_DAYS = 7;
  var DAY_MS = 24 * 60 * 60 * 1000;

  var MONTHS = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    pl: ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru']
  };

  var MONTHS_LONG = {
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    pl: ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia']
  };

  var WEEKDAYS_FALLBACK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var WEEKDAYS_SHORT = {
    en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    pl: ['Nd.', 'Pon.', 'Wt.', 'Śr.', 'Czw.', 'Pt.', 'Sob.']
  };
  var WEEKDAYS_LONG = {
    en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    pl: ['niedziela', 'poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota']
  };
  var WEEKDAYS_GENITIVE = {
    pl: ['niedzieli', 'poniedziałku', 'wtorku', 'środy', 'czwartku', 'piątku', 'soboty']
  };

  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  var config = readConfig();
  var TIMEZONE = config.timezone || FALLBACK_TIMEZONE;
  var STRINGS = config.strings || {};

  function parseTime(value) {
    var raw = value == null ? '' : String(value);
    var match = raw.match(/(\d{1,2})\s*[:.]?\s*(\d{2})?/);
    if (!match) return { hour: 23, minute: 59 };

    var hour = parseInt(match[1], 10) || 0;
    var minute = parseInt(match[2], 10) || 0;

    if (/p\.?\s*m\.?/i.test(raw) && hour < 12) hour += 12;
    if (/a\.?\s*m\.?/i.test(raw) && hour === 12) hour = 0;

    return { hour: Math.min(23, hour), minute: Math.min(59, minute) };
  }

  function buildDays(source) {
    return DAY_KEYS.map(function (key) {
      var row = (source && source[key]) || {};
      var time = parseTime(row.cutoff);
      return {
        key: key,
        enabled: row.enabled === true,
        lead: Math.max(0, parseInt(row.lead, 10) || 0),
        hour: time.hour,
        minute: time.minute
      };
    });
  }

  function anyEnabled(days) {
    for (var i = 0; i < days.length; i++) {
      if (days[i].enabled) return true;
    }
    return false;
  }

  var DAYS = buildDays(config.days);
  var SOURCE = config.days ? 'foodin-app' : 'theme';

  // Legacy: older configs used daysOverride for the app metafield.
  if (config.daysOverride) {
    var override = buildDays(config.daysOverride);
    if (anyEnabled(override)) {
      DAYS = override;
      SOURCE = 'foodin-app';
    }
  }

  /**
   * Manual day-offs (holidays / outages). ISO YYYY-MM-DD strings from theme
   * settings and optional metafield. Hook point for future per-day limits (§14).
   */
  function parseDisabledDates(raw) {
    var set = {};
    function add(v) {
      if (v == null) return;
      var s = String(v).trim();
      var m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) {
        m = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
        if (m) s = m[3] + '-' + pad(parseInt(m[2], 10)) + '-' + pad(parseInt(m[1], 10));
        else return;
      } else {
        s = m[1] + '-' + m[2] + '-' + m[3];
      }
      set[s] = true;
    }
    if (Array.isArray(raw)) {
      raw.forEach(add);
    } else if (typeof raw === 'string') {
      raw.split(/[\n,;]+/).forEach(add);
    }
    return set;
  }

  var DISABLED_DATES = parseDisabledDates(config.disabledDates);
  if (config.disabledDatesOverride) {
    var extra = parseDisabledDates(config.disabledDatesOverride);
    Object.keys(extra).forEach(function (k) {
      DISABLED_DATES[k] = true;
    });
  }

  function shopNowImpl() {
    var real = new Date();
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(real);

      var got = {};
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type !== 'literal') got[parts[i].type] = parseInt(parts[i].value, 10);
      }

      if (got.year && got.month && got.day) {
        var hour = got.hour === 24 ? 0 : got.hour || 0;
        return new Date(got.year, got.month - 1, got.day, hour, got.minute || 0, got.second || 0, 0);
      }
    } catch (e) {
      /* fall through */
    }
    return real;
  }

  // Overridable for tests / rare admin tools — production uses shopNowImpl.
  var getNow = shopNowImpl;

  function shopNow() {
    return getNow();
  }

  function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  function toISODate(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function cutoffFor(date) {
    var row = DAYS[date.getDay()];
    if (!row || !row.enabled) return null;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate() - row.lead,
      row.hour,
      row.minute,
      0,
      0
    );
  }

  function isManuallyDisabled(date) {
    return !!DISABLED_DATES[toISODate(date)];
  }

  function isDeliveryWeekday(date) {
    var row = DAYS[date.getDay()];
    return !!(row && row.enabled);
  }

  /**
   * Extensible availability check for NEW shoppers.
   * Future meal/order caps (§14) plug in here without rewriting the picker.
   */
  function isDateAvailable(date, options) {
    options = options || {};
    if (!date || isNaN(date.getTime())) return false;
    if (!isDeliveryWeekday(date)) return false;
    if (isManuallyDisabled(date)) return false;

    var now = options.now || shopNow();
    if (startOfDay(date).getTime() < startOfDay(now).getTime()) return false;

    var cutoff = cutoffFor(date);
    if (!cutoff || cutoff.getTime() <= now.getTime()) return false;

    // §14 hook — reserved for later capacity limits:
    // if (options.limitReached && options.limitReached(date)) return false;

    return true;
  }

  /**
   * §12: keep a cart-locked date across cart edits only while it is still
   * orderable. Cutoff, manual day-offs, and past days always win — otherwise
   * a date auto-saved before cutoff stays checkoutable all afternoon.
   */
  function honorLockedDate(date, options) {
    return isDateAvailable(date, options);
  }

  function describeDate(date, options) {
    options = options || {};
    var now = options.now || shopNow();
    var lockedIso = options.lockedIso || null;
    var iso = toISODate(date);
    var row = DAYS[date.getDay()];
    var cutoff = cutoffFor(date);
    // Coerce to real booleans — `lockedIso && …` yields null when unlocked,
    // and `open || null` made selectable null (broke disabled-tile checks).
    var isLock = !!(lockedIso && lockedIso === iso && honorLockedDate(date, { now: now }));
    var open = !!isDateAvailable(date, { now: now });
    var selectable = !!(open || isLock);

    var closedReason = null;
    if (!row || !row.enabled) closedReason = 'not_delivery_day';
    else if (isManuallyDisabled(date)) closedReason = 'manual';
    else if (startOfDay(date).getTime() < startOfDay(now).getTime()) closedReason = 'past';
    else if (cutoff && cutoff.getTime() <= now.getTime() && !isLock) closedReason = 'cutoff';

    return {
      date: date,
      iso: iso,
      display: formatDisplayDate(date),
      cutoff: cutoff,
      open: open,
      selectable: selectable,
      locked: !!isLock,
      closedReason: closedReason,
      tileLabel: formatTileLabel(date),
      tileLabelLong: formatTileLabelLong(date),
      selectedLabel: formatSelectedLabel(date)
    };
  }

  /**
   * Delivery weekdays inside [today … today+7].
   * Only still-orderable dates are returned — cut-off-closed / past /
   * manually disabled days are omitted so the picker shows only dates
   * the customer can still use.
   */
  function listWindow(options) {
    options = options || {};
    var now = options.now || shopNow();
    var lockedIso = options.lockedIso || null;
    var out = [];

    for (var offset = 0; offset <= DISPLAY_WINDOW_DAYS; offset++) {
      var date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
      if (!isDeliveryWeekday(date)) continue;
      var desc = describeDate(date, { now: now, lockedIso: lockedIso });
      if (!desc.selectable) continue;
      out.push(desc);
    }
    return out;
  }

  function next(options) {
    options = options || {};
    var now = options.now || shopNow();

    for (var offset = 0; offset <= LOOKAHEAD_DAYS; offset++) {
      var date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
      if (!isDateAvailable(date, { now: now })) continue;
      var cutoff = cutoffFor(date);
      return { date: date, cutoff: cutoff, now: now, iso: toISODate(date) };
    }
    return null;
  }

  function localeCode() {
    return document.documentElement.lang || config.locale || 'pl';
  }

  function shortLocale() {
    return localeCode().substring(0, 2).toLowerCase();
  }

  function formatDate(date) {
    var lang = shortLocale();
    var day = date.getDate();
    var month = (MONTHS[lang] || MONTHS.en)[date.getMonth()];
    var year = date.getFullYear();

    if (lang === 'en') {
      var suffix =
        day === 1 || day === 21 || day === 31
          ? 'st'
          : day === 2 || day === 22
            ? 'nd'
            : day === 3 || day === 23
              ? 'rd'
              : 'th';
      return day + suffix + ' ' + month + ' ' + year + '.';
    }

    return day + ' ' + month + ' ' + year + '.';
  }

  function formatDisplayDate(date) {
    return pad(date.getDate()) + '.' + pad(date.getMonth() + 1) + '.' + date.getFullYear();
  }

  function parseDisplayDate(value) {
    if (!value) return null;
    var s = String(value).trim();
    var m = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
    if (m) {
      return new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10));
    }
    m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    }
    return null;
  }

  function formatTileLabel(date) {
    var lang = shortLocale();
    var short = (WEEKDAYS_SHORT[lang] || WEEKDAYS_SHORT.en)[date.getDay()];
    return short + ' ' + pad(date.getDate()) + '.' + pad(date.getMonth() + 1);
  }

  function formatTileLabelLong(date) {
    var lang = shortLocale();
    var long = (WEEKDAYS_LONG[lang] || WEEKDAYS_LONG.en)[date.getDay()];
    return long.toUpperCase() + ' ' + pad(date.getDate()) + '.' + pad(date.getMonth() + 1);
  }

  function formatSelectedLabel(date) {
    var lang = shortLocale();
    var weekday = (WEEKDAYS_LONG[lang] || WEEKDAYS_LONG.en)[date.getDay()];
    var month = (MONTHS_LONG[lang] || MONTHS_LONG.en)[date.getMonth()];
    if (lang === 'pl') {
      return weekday + ', ' + date.getDate() + ' ' + month;
    }
    return weekday + ', ' + month + ' ' + date.getDate();
  }

  function formatWeekday(date) {
    var declined = WEEKDAYS_GENITIVE[shortLocale()];
    if (declined) return declined[date.getDay()];

    try {
      return new Intl.DateTimeFormat(localeCode(), { weekday: 'long' }).format(date);
    } catch (e) {
      return WEEKDAYS_FALLBACK[date.getDay()];
    }
  }

  function formatCutoff(result) {
    if (!result || !result.cutoff || !result.now) return '';

    var msLeft = result.cutoff.getTime() - result.now.getTime();
    // Past cutoff: hide the hint (locked days are reassigned once cutoff hits).
    if (msLeft <= 0) return '';

    var clock = pad(result.cutoff.getHours()) + ':' + pad(result.cutoff.getMinutes());

    if (msLeft < DAY_MS) {
      var minutesLeft = Math.floor(msLeft / 60000);
      var hours = Math.floor(minutesLeft / 60);
      var minutes = minutesLeft % 60;
      var remaining =
        hours > 0
          ? minutes > 0
            ? hours + ' h ' + minutes + ' min'
            : hours + ' h'
          : minutes + ' min';
      return (STRINGS.order_within || 'Order within') + ' ' + remaining;
    }

    return (STRINGS.order_until || 'Order by') + ' ' + formatWeekday(result.cutoff) + ', ' + clock;
  }

  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  function setHidden(el, hidden) {
    if (hidden) {
      if (el.style.getPropertyValue('display') !== 'none') {
        el.style.setProperty('display', 'none', 'important');
      }
    } else if (el.style.getPropertyValue('display') === 'none') {
      el.style.removeProperty('display');
    }
  }

  function update() {
    var result = next();

    var badges = document.querySelectorAll('[data-delivery-badge]');
    for (var i = 0; i < badges.length; i++) {
      setHidden(badges[i], !result);
    }

    if (!result) return result;

    var dateEls = document.querySelectorAll('[data-delivery-date]');
    for (var j = 0; j < dateEls.length; j++) {
      // Prefer cart-selected date on picker-aware badges; plain badges keep next().
      var preferSelected = dateEls[j].closest('[data-delivery-date-picker]');
      if (!preferSelected) setText(dateEls[j], formatDate(result.date));
    }

    var cutoffEls = document.querySelectorAll('[data-delivery-cutoff]');
    var cutoffText = formatCutoff(result);
    for (var k = 0; k < cutoffEls.length; k++) {
      if (cutoffEls[k].closest('[data-delivery-date-picker]')) continue;
      setText(cutoffEls[k], cutoffText);
      setHidden(cutoffEls[k], !cutoffText);
    }

    return result;
  }

  window.DeliverySchedule = {
    config: config,
    days: DAYS,
    source: SOURCE,
    timezone: TIMEZONE,
    displayWindowDays: DISPLAY_WINDOW_DAYS,
    disabledDates: DISABLED_DATES,
    next: next,
    listWindow: listWindow,
    describeDate: describeDate,
    isDateAvailable: isDateAvailable,
    honorLockedDate: honorLockedDate,
    isDeliveryWeekday: isDeliveryWeekday,
    isManuallyDisabled: isManuallyDisabled,
    cutoffFor: cutoffFor,
    update: update,
    formatDate: formatDate,
    formatDisplayDate: formatDisplayDate,
    parseDisplayDate: parseDisplayDate,
    formatTileLabel: formatTileLabel,
    formatTileLabelLong: formatTileLabelLong,
    formatSelectedLabel: formatSelectedLabel,
    formatCutoff: formatCutoff,
    toISODate: toISODate,
    shopNow: shopNow,
    /** @private test helper */
    __setNow: function (fn) {
      getNow = typeof fn === 'function' ? fn : shopNowImpl;
    }
  };

  window.updateCartDrawerDeliveryInfo = update;

  function start() {
    update();
    setInterval(update, 30 * 1000);

    ['cart:refresh', 'cart:update', 'cart:change'].forEach(function (name) {
      document.addEventListener(name, update);
    });

    if (typeof subscribe === 'function') {
      try {
        subscribe('cart-update', update);
      } catch (e) {
        /* pub/sub optional */
      }
    }

    if (window.MutationObserver && document.body) {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          update();
          if (window.DeliveryDatePicker && typeof window.DeliveryDatePicker.refresh === 'function') {
            window.DeliveryDatePicker.refresh();
          }
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
